import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './chapterRefreshNotice.css'

import { commonResource } from '../../../i18n/common'
import { ErrorModal } from '../modals/errorModal'
import { ContinueModal } from '../modals/continueModal'
import { LoadingScreen } from '../loading/loadingScreen'
import { missionManager as realMissionManager } from '../../blocks/mission'
import {
  createProject,
  getProjectInfo,
  getProjectWorkspaceData,
  saveProjectWorkspaceData,
  updateProjectInfo,
  getProjectList,
  findExistingProject,
} from './api'
import missionProjects from '../../missions'
import { toSiteAliasSlug } from '../preview/previewDisplayMode'
import { isStage1TutorialMission } from '../../utils/tabletSupportedMission'
import { useTranslator } from '../translator/hook'
import { generateCodeHeadless } from '../../blocks/blocks'
import { MissionResponseData } from '../../openapi'
import { useDigitaneApi } from './digitaneApi'
import { useDelayedLoading } from './hook'
import { UserContext } from '../user/provider'
import { generateProjectTitle } from '../../utils/projectTitleUtils'
import {
  AEON_SLUG,
  PARTS_HIDDEN_VALUE,
  PARTS_PARAM,
  START_PARAMS,
} from '../../constants/constants'
import { buildProjectQuery } from './projectQuery'

const AUTO_SAVE_DEBOUNCE = 60000

interface ProjectContextType {
  canEdit: boolean
  isOtherSiteMission: boolean
  copyAndOpenProject: () => Promise<void>
  createAndOpenProject: () => Promise<void>
  loading: boolean
  missionManager: typeof realMissionManager
  projectId: string
  saveProject: () => Promise<boolean>
  saving: boolean
  title: string
  updateProjectShareStatus: (isPublic: boolean) => Promise<boolean>
  updateProjectTitle: (title: string) => Promise<string>
  userId: string
  missionInfo: MissionResponseData | undefined
  updateChapters: () => Promise<MissionResponseData | undefined>
  isPublic: boolean
  isCopy: boolean
  originalId: string | undefined
  isTutorialMission: boolean
}

const makeMissionManagerProxy = () => new Proxy(realMissionManager, {})

// ?vimeoId= は互換のため受け付け続ける。ここで決めた index の seq を Widget の chapterSeq に渡す
const getInitialChapterIndex = (
  sortedChapters: MissionResponseData['chapters'],
  vimeoId: string | null,
): number => {
  const vimeoIndex = sortedChapters.findIndex((ch) => ch.videoCode === vimeoId)
  if (vimeoIndex >= 0) return vimeoIndex

  const unwatchedIndex = sortedChapters.findIndex(
    (ch) => !ch.currentChapterResult,
  )
  if (unwatchedIndex >= 0) return unwatchedIndex

  return 0
}

export const ProjectContext = createContext<ProjectContextType>({
  canEdit: false,
  isOtherSiteMission: false,
  copyAndOpenProject: async () => {},
  createAndOpenProject: async () => {},
  loading: true,
  missionManager: makeMissionManagerProxy(),
  projectId: '',
  saveProject: async () => false,
  saving: false,
  title: '',
  updateProjectShareStatus: async () => false,
  updateProjectTitle: async () => '',
  userId: '',
  missionInfo: undefined,
  updateChapters: async (): Promise<MissionResponseData | undefined> => {
    return undefined
  },
  isPublic: false,
  isCopy: false,
  originalId: undefined,
  isTutorialMission: false,
})

export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const isPartsUnlocked = searchParams.get(PARTS_PARAM) === PARTS_HIDDEN_VALUE
  const shouldUnlockParts = useCallback(
    (mission: MissionResponseData | undefined) =>
      isPartsUnlocked && mission?.slug === AEON_SLUG && mission?.num === 1,
    [isPartsUnlocked],
  )
  const { t } = useTranslator()
  const { getStageInfo } = useDigitaneApi()
  const { userId, siteName, isOnlineUser } = useContext(UserContext)
  //即時反映すると、ローディングがちらつくため、遅延ローディングを使用
  //プロジェクト読み込みが予定されている場合、初回レンダーでワークスペースが
  //一瞬表示されないよう初期値をtrueにする
  const [loading, setLoading] = useDelayedLoading(
    Boolean(searchParams.get('projectId') || searchParams.get('missionId')),
  )
  const [error, setError] = useState<Error | null>(null)
  const [missionManager, setMissionManager] = useState(
    makeMissionManagerProxy(),
  )
  const [projectId, setProjectId] = useState('')
  const [projectOwnerId, setProjectOwnerId] = useState('')
  const [missionId, setMissionId] = useState<string>('')
  const [chapterRefreshFailed, setChapterRefreshFailed] = useState(false)
  const [chapterRefreshPending, setChapterRefreshPending] = useState(false)
  const chapterRefreshVersion = useRef(0)
  const chapterRefreshInFlight = useRef(false)

  // 別の作品への移動・アンマウント後に古い取り直し結果を反映しない。
  useEffect(() => {
    chapterRefreshVersion.current += 1
    chapterRefreshInFlight.current = false
    setChapterRefreshFailed(false)
    setChapterRefreshPending(false)
    return () => {
      chapterRefreshVersion.current += 1
      chapterRefreshInFlight.current = false
    }
  }, [missionId, projectId])
  const [saving, setSaving] = useState(false)
  const timeout = useRef<ReturnType<typeof setTimeout>>()
  const [title, setTitle] = useState(t('default_title'))
  const [isPublic, setIsPublic] = useState(false)
  const [isOtherSiteMission, setIsOtherSiteMission] = useState(false)
  const [isCopy, setIsCopy] = useState(false)
  const [originalId, setOriginalId] = useState<string | undefined>(undefined)
  const canEdit = !!userId && userId === projectOwnerId
  const isCreatingRef = useRef(false)

  const withCreatingGuard = useCallback(async (fn: () => Promise<void>) => {
    if (isCreatingRef.current) return
    try {
      isCreatingRef.current = true
      await fn()
    } finally {
      isCreatingRef.current = false
    }
  }, [])

  const [showContinueModal, setShowContinueModal] = useState(false)
  const [pendingExistingProjectId, setPendingExistingProjectId] = useState<
    string | null
  >(null)
  const [pendingMissionId, setPendingMissionId] = useState<string>('')

  const [missionInfo, setMissionInfo] = useState<
    MissionResponseData | undefined
  >(undefined)

  const isTutorialMission = isStage1TutorialMission(missionInfo) && !isCopy

  const makeNewProxy = useCallback(() => {
    setMissionManager(makeMissionManagerProxy())
  }, [])

  // Make a new proxy to trigger a re-render when the mission manager changes.
  useEffect(() => {
    realMissionManager.on('change', makeNewProxy)
    makeNewProxy()
    return () => {
      realMissionManager.off('change', makeNewProxy)
    }
  }, [makeNewProxy])

  const createAndOpenProject_ = useCallback(
    async (projectId: string, missionId: string, userId: string) => {
      setLoading(true)

      const error = new Error(commonResource.to('project_create_error'))

      try {
        let missionInfo: MissionResponseData | undefined

        if (missionId) {
          //Note:コンテンツチームがローカルテストできるように
          if (location.hostname !== 'localhost' && isNaN(Number(missionId))) {
            throw new Error(`missionId is not a number. missionId:${missionId}`)
          }
          const result = await getStageInfo(missionId)
          if (result) {
            setMissionInfo(result)
            missionInfo = result
          }
        }

        if (projectId) {
          await saveProjectWorkspaceData(projectId, realMissionManager.saveData)
        }

        const existingProjects = await getProjectList(userId)

        const title = await generateProjectTitle(
          missionInfo,
          false,
          undefined,
          existingProjects,
        )

        const newProjectId = await createProject({
          missionId,
          title: title,
          userId,
        })
        if (newProjectId) {
          navigate(buildProjectQuery(newProjectId, searchParams))
        } else {
          console.error(
            'No project id returned from createProject',
            newProjectId,
          )
          setError(error)
        }
      } catch (e) {
        console.error('Error creating project:', e)
        setError(error)
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, searchParams],
  )

  // 既存プロジェクトがあれば開き、なければ新規作成
  const openExistingOrCreateNew = useCallback(
    async (
      existingProjectId: string | null,
      missionId: string,
      userId: string,
    ) => {
      if (existingProjectId) {
        // 既存プロジェクトがあれば開く
        navigate(buildProjectQuery(existingProjectId, searchParams))
      } else {
        // 既存プロジェクトがなければ新規作成
        await createAndOpenProject_('', missionId, userId)
      }
    },
    [createAndOpenProject_, navigate, searchParams],
  )

  // startパラメータに基づいてプロジェクトを開くか作成するかを決定
  const handleProjectInitialization = useCallback(
    async (start: string, missionId: string, userId: string) => {
      // start パラメータによる処理の分岐
      // - パラメータなし または start=init → 常に新規作成
      // - start=continue → 既存があれば開く、なければ新規作成
      // - start=confirm → 将来的には確認モーダルを表示（現状は continue と同じ挙動）

      if (start === START_PARAMS.INIT) {
        // 常に新規プロジェクトを作成
        await createAndOpenProject_('', missionId, userId)
      } else if (start === START_PARAMS.CONTINUE) {
        // 既存プロジェクトを探す
        // 検索中にワークスペース画面が表示されないようローディングを表示する
        setLoading(true)
        const existingProjectId = await findExistingProject(missionId)
        await openExistingOrCreateNew(existingProjectId, missionId, userId)
      } else if (start === START_PARAMS.CONFIRM) {
        // 既存プロジェクトを探す
        setLoading(true)
        const existingProjectId = await findExistingProject(missionId)
        if (existingProjectId) {
          // 既存プロジェクトがある場合はモーダルを表示
          setPendingExistingProjectId(existingProjectId)
          setPendingMissionId(missionId)
          setShowContinueModal(true)
          setLoading(false)
        } else {
          // 既存プロジェクトがない場合は新規作成
          await createAndOpenProject_('', missionId, userId)
        }
      } else {
        // 不明なパラメータの場合はデフォルト動作（新規作成）
        await createAndOpenProject_('', missionId, userId)
      }
    },
    [createAndOpenProject_, openExistingOrCreateNew, setLoading],
  )

  // Get the project ID from the query string.
  useEffect(() => {
    const initializeProject = async () => {
      // Reset the state when the project ID changes.
      setMissionId('')
      setProjectOwnerId('')
      setTitle('')
      setIsOtherSiteMission(false)
      // 前プロジェクトの missionInfo / isCopy を持ち越さない。タブレットの
      // ミッションガード（editor.tsx）が stale な isTutorialMission（isCopy にも
      // 依存）で誤判定するのを防ぐ。isCopy はプロジェクト取得時に再設定される。
      setMissionInfo(undefined)
      setIsCopy(false)
      // Get the project id from the url.
      const projectId = searchParams.get('projectId') ?? ''
      setProjectId(projectId)

      // If there's no project ID, check for a mission ID.
      if (!projectId) {
        const missionId = searchParams.get('missionId') ?? ''
        setMissionId(missionId)
        if (missionId && userId) {
          const start = searchParams.get('start') ?? START_PARAMS.INIT
          await withCreatingGuard(() =>
            handleProjectInitialization(start, missionId, userId),
          )
        } else {
          // If there's no project or mission ID, navigate to the list.
          navigate('/list')
        }
      }
    }
    initializeProject()
  }, [
    handleProjectInitialization,
    navigate,
    searchParams,
    userId,
    withCreatingGuard,
  ])

  // Save the project when a workspace changes.
  useEffect(() => {
    if (!canEdit || !projectId) return

    // Close over the current save data, so the correct data is saved when the project is saved.
    let payload = realMissionManager.saveData
    const handleSave = async () => {
      setSaving(true)
      try {
        await saveProjectWorkspaceData(projectId, payload)
      } catch (e) {
        console.error('Error saving project:', e)
        toast.error(commonResource.to('project_save_error'))
      }
      setSaving(false)
    }

    // Trigger a save when the workspace changes, but debounce it.
    const handleWorkspaceChange = () => {
      // Make sure the payload is up to date.
      payload = realMissionManager.saveData
      clearTimeout(timeout.current)
      timeout.current = setTimeout(handleSave, AUTO_SAVE_DEBOUNCE)
      // setSaving(true) //ブロック編集後に保存中のローディングを出したい場合はコメントを外す
    }
    realMissionManager.on('workspace', handleWorkspaceChange)

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleSave()
      }
    }
    addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      // Save the project when the component unmounts or the project ID changes.
      handleSave()
      realMissionManager.off('workspace', handleWorkspaceChange)
      removeEventListener('visibilitychange', handleVisibilityChange)
      clearTimeout(timeout.current)
    }
  }, [projectId, canEdit])

  // Show a warning if the user tries to navigate away with unsaved changes.
  useEffect(() => {
    if (!saving) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = 'warn'
      return 'warn'
    }
    addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [saving])

  // Fetch the project data when the project ID changes.
  useEffect(() => {
    if (!projectId || !siteName) {
      // siteNameが未取得（ユーザー情報の取得失敗等）の場合はプロジェクトを
      // 読み込めないため、初期値trueのloadingが残らないよう解除する
      if (projectId && !siteName) {
        setLoading(false)
      }
      return
    }

    setError(null)
    setLoading(true)

    // Fetch the project data when the project ID changes.
    Promise.all([getProjectInfo(projectId), getProjectWorkspaceData(projectId)])
      .then(async ([projectInfo, workspaceData]) => {
        if (!projectInfo) {
          setError(new Error(`${commonResource.to('invalid_mission_id')}`))
          return
        } else if (!workspaceData) {
          setError(new Error(commonResource.to('no_workspace_data')))
          return
        }

        // アクセス権限をチェック
        const isOwner = userId === projectInfo.userId
        const hasAccess = isOwner || projectInfo.isPublic

        if (!hasAccess) {
          setError(new Error(commonResource.to('error_not_shared_project')))
          return
        }
        setIsCopy(projectInfo.isCopy ?? false)
        setOriginalId(projectInfo.originalId)

        let currentMissionInfo = missionInfo
        if (currentMissionInfo === undefined && projectInfo.missionId) {
          currentMissionInfo = await getStageInfo(projectInfo.missionId)
          setMissionInfo(currentMissionInfo)
        }

        let missionKey = getMissionKey(currentMissionInfo)

        const missionSiteName = currentMissionInfo?.siteName

        // ミッションのsiteNameが存在しない、または対応するmissionProjectsが存在しない場合はエラー
        if (!missionSiteName) {
          setError(
            new Error(
              `missionSiteName is undefined - ${commonResource.to('invalid_mission_id')}`,
            ),
          )
          return
        }

        if (!missionProjects[missionSiteName]) {
          setError(
            new Error(
              `missionProjects for site '${missionSiteName}' not found - ${commonResource.to('invalid_mission_id')}`,
            ),
          )
          return
        }

        if (!missionProjects[missionSiteName][missionKey]) {
          // mission_idが有効でもmissionKeyが見つからない場合、スラッグを確認
          if (currentMissionInfo?.slug && currentMissionInfo?.num) {
            const alternativeKey = `${toSiteAliasSlug(currentMissionInfo.slug, missionSiteName)}_${currentMissionInfo.num}`
            if (missionProjects[missionSiteName][alternativeKey]) {
              missionKey = alternativeKey
            } else {
              setError(
                new Error(
                  `Mission key '${alternativeKey}' not found for site '${missionSiteName}' - ${commonResource.to('invalid_mission_id')}`,
                ),
              )
              return
            }
          } else {
            setError(
              new Error(
                `Invalid mission info - ${commonResource.to('invalid_mission_id')}`,
              ),
            )
            return
          }
        }

        setMissionId(projectInfo.missionId)
        setTitle(projectInfo.title)
        setProjectOwnerId(projectInfo.userId)
        // NOTE: ローカルテスト用
        if (location.hostname === 'localhost') {
          if (isOnlineUser) {
            setProjectOwnerId('1')
          } else {
            setProjectOwnerId('2')
          }
        }

        setIsPublic(!!projectInfo.isPublic)

        // 他のサイトのミッションかどうかをチェック
        if (missionSiteName !== siteName) {
          setIsOtherSiteMission(true)
        }

        // Set everything in the mission manager.
        // Note: missionSiteNameは既に上でチェック済み
        const { chapters, objects, template, toolbox } =
          missionProjects[missionSiteName][missionKey]

        //プロジェクトチャプター情報にDB情報をセット
        const sortedChapters =
          currentMissionInfo?.chapters.sort(
            (a, b) => (a.seq || 0) - (b.seq || 0),
          ) || []
        chapters.forEach((item, i) => {
          item.chapter = sortedChapters[i]
        })
        if (currentMissionInfo?.chapters.length !== chapters.length) {
          setError(
            new Error(
              `CodingBugs__チャプターのブロック情報_${chapters.length}とビデオの情報_${currentMissionInfo?.chapters.length}が一致しません`,
            ),
          )
          return
        }
        // ?parts=0 の Aeon M1（rj1_trial_1）では全パーツを表示する（チャプター別ツールボックス制限を無効化）
        realMissionManager.chapters =
          shouldUnlockParts(currentMissionInfo) ?
            chapters.map((c) => ({ ...c, toolbox: undefined }))
          : chapters

        realMissionManager.objects = objects.map((obj) => ({
          // Merge the saved workspaces into the hardcoded mission data.
          ...obj,
          ...(workspaceData[obj.name] ?? {}),
        }))
        // Ensure that if the object has a workspace, it has generated code.
        realMissionManager.objects.forEach((obj) => {
          if (obj.workspace) obj.code = generateCodeHeadless(obj.workspace)
        })
        realMissionManager.template = template
        realMissionManager.toolbox = toolbox ?? null

        realMissionManager.chapterIndex =
          isTutorialMission ? 1 : (
            getInitialChapterIndex(sortedChapters, searchParams.get('vimeoId'))
          )
      })
      .catch((err) => {
        console.error('Error fetching project data:', err)
        setError(new Error(commonResource.to('project_fetch_error')))
      })
      .finally(() => {
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId, projectId, siteName, userId, navigate, shouldUnlockParts])

  const getMissionKey = useCallback(
    (mission: MissionResponseData | undefined) => {
      return `${mission?.slug}_${mission?.num}`
    },
    [],
  )

  const clearError = useCallback(() => {
    // 非公開プロジェクトへのアクセスエラーの場合、作品一覧に遷移
    if (error?.message === commonResource.to('error_not_shared_project')) {
      navigate('/list')
    }
    setError(null)
  }, [error, navigate])

  const handleContinueFromModal = useCallback(() => {
    if (pendingExistingProjectId) {
      // プロジェクト読み込みが始まるまでワークスペースが表示されないようにする
      setLoading(true)
      navigate(buildProjectQuery(pendingExistingProjectId, searchParams))
    }
    setShowContinueModal(false)
    setPendingExistingProjectId(null)
    setPendingMissionId('')
  }, [navigate, pendingExistingProjectId, searchParams, setLoading])

  const handleNewFromModal = useCallback(async () => {
    setShowContinueModal(false)
    if (pendingMissionId && userId) {
      await withCreatingGuard(() =>
        createAndOpenProject_('', pendingMissionId, userId),
      )
    }
    setPendingExistingProjectId(null)
    setPendingMissionId('')
  }, [createAndOpenProject_, pendingMissionId, userId, withCreatingGuard])

  // Callback to trigger an immediate save from elsewhere in the app.
  const saveProject = useCallback(async () => {
    if (!canEdit) return false

    setSaving(true)
    // We're saving immediately, so clear the debounce timeout.
    clearTimeout(timeout.current)
    try {
      await saveProjectWorkspaceData(projectId, realMissionManager.saveData)
      return true
    } catch (e) {
      console.error('Error saving project:', e)
      setError(new Error(commonResource.to('project_save_error')))
    } finally {
      setSaving(false)
    }
    return false
  }, [canEdit, projectId])

  const copyAndOpenProject = useCallback(async () => {
    setLoading(true)

    const error = new Error(commonResource.to('project_copy_error'))
    try {
      if (canEdit) {
        await saveProjectWorkspaceData(projectId, realMissionManager.saveData)
      }

      const existingProjects = await getProjectList(userId)

      const newTitle = await generateProjectTitle(
        missionInfo,
        !canEdit,
        title,
        existingProjects,
      )

      if (missionInfo?.siteName !== siteName) {
        setIsOtherSiteMission(true)
      }

      const newProjectId = await createProject({
        missionId,
        title: newTitle,
        userId,
        isCopy: true,
        originalId: projectId,
      })
      if (newProjectId) {
        await saveProjectWorkspaceData(
          newProjectId,
          realMissionManager.saveData,
        )
        navigate(buildProjectQuery(newProjectId, searchParams))
      } else {
        console.error('No project id returned from createProject', newProjectId)
        setError(error)
      }
    } catch (e) {
      console.error('Error creating project:', e)
      setError(error)
    }
    setLoading(false)
  }, [
    setLoading,
    canEdit,
    userId,
    missionInfo,
    title,
    siteName,
    missionId,
    projectId,
    navigate,
    searchParams,
  ])

  const createAndOpenProject = useCallback(async () => {
    return createAndOpenProject_(projectId, missionId, userId)
  }, [createAndOpenProject_, missionId, projectId, userId])

  const updateProjectTitle = useCallback(
    async (newTitle: string) => {
      if (!canEdit) return title

      try {
        const results = await updateProjectInfo(projectId, {
          title: newTitle,
          userId,
        })
        newTitle = results?.title ?? title
        setTitle(newTitle)
        return newTitle
      } catch (e) {
        console.error('Error updating project title:', e)
        setError(new Error(commonResource.to('project_update_error')))
      }
      return title
    },
    [canEdit, projectId, title, userId],
  )

  const updateProjectShareStatus = useCallback(
    async (isPublic: boolean) => {
      if (!canEdit) return false

      try {
        const results = await updateProjectInfo(projectId, {
          isPublic,
          userId,
        })
        setIsPublic(!!results?.isPublic)
        return !!results?.isPublic
      } catch (e) {
        console.error('Error updating project share status:', e)
        setError(new Error(commonResource.to('project_update_error')))
        return false
      }
    },
    [canEdit, projectId, userId],
  )

  const updateChapters = useCallback(async (): Promise<
    MissionResponseData | undefined
  > => {
    if (!missionId || missionId === '') return

    const version = ++chapterRefreshVersion.current
    chapterRefreshInFlight.current = true
    setChapterRefreshPending(true)
    let currentMissionInfo: MissionResponseData | undefined
    try {
      // 初期表示など他の API 呼び出しのエラー遷移は変更しない。
      currentMissionInfo = await getStageInfo(missionId, {
        redirectOnError: false,
      })
    } catch {
      // HTTP エラー以外の例外でも、Widget のイベントから未処理の拒否を出さない。
      currentMissionInfo = undefined
    }
    // より新しい取り直し・作品の切り替え・アンマウントがあれば捨てる。
    if (version !== chapterRefreshVersion.current) return
    chapterRefreshInFlight.current = false
    setChapterRefreshPending(false)
    // HTTP / 通信エラーでも表示中の情報とエディタを保ち、手動で再試行できる。
    if (currentMissionInfo === undefined) {
      setChapterRefreshFailed(true)
      console.warn(
        `[WARN] ミッション情報を取り直せなかったため、表示中の情報を使い続けます (missionId: ${missionId})`,
      )
      return
    }
    setChapterRefreshFailed(false)
    setMissionInfo(currentMissionInfo)
    let missionKey = getMissionKey(currentMissionInfo)

    const missionSiteName = currentMissionInfo?.siteName

    if (!missionSiteName) {
      setError(
        new Error(
          `missionSiteName is undefined - ${commonResource.to('invalid_mission_id')}`,
        ),
      )
      return
    }

    if (!missionProjects[missionSiteName]) {
      setError(
        new Error(
          `missionProjects for site '${missionSiteName}' not found - ${commonResource.to('invalid_mission_id')}`,
        ),
      )
      return
    }

    if (!missionProjects[missionSiteName][missionKey]) {
      // mission_idが有効でもmissionKeyが見つからない場合、スラッグを確認
      if (currentMissionInfo?.slug && currentMissionInfo?.num) {
        const alternativeKey = `${toSiteAliasSlug(currentMissionInfo.slug, missionSiteName)}_${currentMissionInfo.num}`
        if (missionProjects[missionSiteName][alternativeKey]) {
          missionKey = alternativeKey
        } else {
          setError(
            new Error(
              `Mission key '${alternativeKey}' not found for site '${missionSiteName}' - ${commonResource.to('invalid_mission_id')}`,
            ),
          )
          return
        }
      } else {
        setError(
          new Error(
            `Invalid mission info - ${commonResource.to('invalid_mission_id')}`,
          ),
        )
        return
      }
    }

    const { chapters, template, toolbox } =
      missionProjects[missionSiteName][missionKey]

    //プロジェクトチャプター情報にDB情報をセット
    const sortedChapters =
      currentMissionInfo?.chapters.sort(
        (a, b) => (a.seq || 0) - (b.seq || 0),
      ) || []
    chapters.forEach((item, i) => {
      item.chapter = sortedChapters[i]
    })
    if (currentMissionInfo?.chapters.length !== chapters.length) {
      setError(
        new Error(
          'CodingBugs　チャプターのブロック情報とビデオの情報が一致しません',
        ),
      )
      return
    }
    // chapters を入れ替えると位置が 0 に戻る。取り直しの間に利用者が変えたチャプター
    // （動画窓の Widget での切り替えなど）を保つため、位置は取得前ではなく、
    // 取得後の入れ替える直前に読んで書き戻す
    const currentChapterIndex = realMissionManager.chapterIndex
    realMissionManager.chapters =
      shouldUnlockParts(currentMissionInfo) ?
        chapters.map((c) => ({ ...c, toolbox: undefined }))
      : chapters
    realMissionManager.chapterIndex = currentChapterIndex
    realMissionManager.template = template
    realMissionManager.toolbox = toolbox ?? null

    return currentMissionInfo
  }, [missionId, getStageInfo, getMissionKey, shouldUnlockParts])

  return (
    <ProjectContext.Provider
      value={{
        canEdit,
        copyAndOpenProject,
        createAndOpenProject,
        loading,
        missionManager,
        projectId,
        saveProject,
        saving,
        title,
        updateProjectShareStatus,
        updateProjectTitle,
        userId,
        missionInfo,
        updateChapters,
        isPublic,
        isOtherSiteMission,
        isCopy,
        originalId,
        isTutorialMission,
      }}
    >
      {loading && <LoadingScreen />}
      {showContinueModal ?
        <ContinueModal
          onContinue={handleContinueFromModal}
          onNew={handleNewFromModal}
        />
      : <>
          <ErrorModal
            error={error?.message || null}
            modalTitle={t('not_connected_error')}
            onClose={clearError}
          />
          {chapterRefreshFailed && (
            <div className="chapter-refresh-notice" role="alert">
              <span>{t('chapter_refresh_error')}</span>
              <button
                type="button"
                disabled={chapterRefreshPending}
                onClick={() => {
                  if (!chapterRefreshInFlight.current) void updateChapters()
                }}
              >
                {t('chapter_refresh_retry')}
              </button>
            </div>
          )}
          {children}
        </>
      }
    </ProjectContext.Provider>
  )
}
